// Theatre setup: the rooms, the vocabularies, the room blocks, the billing
// parameters, and the maintenance log.
//
// Nothing here is ever deleted. A retired room or vocabulary entry is
// deactivated, because closed cases reference it and a theatre register has to
// stay readable years later.
//
// Department and the medical-service (procedure) master are NOT here — OT reuses
// the EMR's own, so surgeries reconcile with the rest of the system.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTabBar,
  IpdTextField,
} from "../../ipd/utils";
import { formatIstDateTime } from "../../../utils/istDateTime";
import {
  cancelRoomBlock,
  createAttendeeType,
  createBillingParameter,
  createRoom,
  createRoomBlock,
  createVocab,
  deactivateAttendeeType,
  deactivateBillingParameter,
  deactivateRoom,
  deactivateVocab,
  fetchBillingParameters,
  fetchEnvironmentLogs,
  fetchMaintenanceLogs,
  fetchRoomBlocks,
  fetchSetup,
  createNoteTemplate,
  fetchNoteTemplates,
  recordEnvironmentReading,
  retireNoteTemplate,
  recordMaintenance,
} from "../api/otApi";
import OtScreen from "../components/OtScreen";
import { OtSelect, errorMessage, report } from "../components/common";
import { CLASSIFICATION_OT_ROOMS, MAINTENANCE_TYPES, humanise } from "../constants";
import ChargeServiceAutocomplete from "../../ot-charge-capture/ChargeServiceAutocomplete";

const TABS = [
  { id: "rooms", label: "Operating rooms" },
  { id: "vocab", label: "Vocabularies" },
  { id: "attendee", label: "Case roles" },
  { id: "blocks", label: "Room blocks" },
  { id: "billing", label: "Billing parameters" },
  { id: "maintenance", label: "Maintenance log" },
  { id: "environment", label: "Environment log" },
  { id: "templates", label: "Note templates" },
];

export default function OtMasters() {
  const [tab, setTab] = useState("rooms");
  const [setup, setSetup] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [logs, setLogs] = useState([]);
  const [environment, setEnvironment] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [s, b, p, l, e, t] = await Promise.all([
        fetchSetup(),
        fetchRoomBlocks({ active_only: 1 }),
        fetchBillingParameters(),
        fetchMaintenanceLogs(),
        // Today only. The register's purpose here is "has this theatre been
        // read today"; the historical view is the report, not this screen.
        fetchEnvironmentLogs(),
        fetchNoteTemplates(),
      ]);
      setSetup(s);
      setBlocks(b || []);
      setParameters(p || []);
      setLogs(l || []);
      setEnvironment(e || null);
      setTemplates(t || null);
    } catch (err) {
      setError(errorMessage(err, "Could not load the OT setup"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rooms = setup?.operating_rooms || [];

  return (
    <OtScreen
      title="OT Masters"
      description="Theatres, vocabularies and configuration. Retiring keeps the record."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <IpdTabBar
        tabs={TABS}
        activeTab={tab}
        onChange={setTab}
        ariaLabel="Theatre setup sections"
      />

      {tab === "rooms" && <RoomsTab rooms={rooms} departments={setup?.departments || []} onDone={load} />}
      {tab === "vocab" && <VocabTab setup={setup} onDone={load} />}
      {tab === "attendee" && <AttendeeTab types={setup?.attendee_types || []} onDone={load} />}
      {tab === "blocks" && <BlocksTab blocks={blocks} rooms={rooms} onDone={load} />}
      {tab === "billing" && (
        <BillingTab
          parameters={parameters}
          departments={setup?.departments || []}
          onDone={load}
        />
      )}
      {tab === "maintenance" && <MaintenanceTab logs={logs} rooms={rooms} onDone={load} />}
      {tab === "templates" && (
        <TemplatesTab
          templates={templates?.templates || []}
          availableFields={templates?.available_fields || {}}
          onDone={load}
        />
      )}
      {tab === "environment" && (
        <EnvironmentTab environment={environment} rooms={rooms} onDone={load} />
      )}
    </OtScreen>
  );
}

/** A list of rows with an Add button, shared by every master tab. */
function MasterList({ title, subtitle, onAdd, addLabel, children }) {
  return (
    <IpdSectionCard
      title={title}
      subtitle={subtitle}
      actions={
        onAdd && (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
            {addLabel || "Add"}
          </Button>
        )
      }
    >
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </IpdSectionCard>
  );
}

function Row({ primary, secondary, active = true, onDeactivate, deactivateLabel = "Deactivate" }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        borderBottom: `1px solid ${IPD_COLORS.border}`,
        opacity: active ? 1 : 0.55,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{primary}</Typography>
        {secondary && (
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>{secondary}</Typography>
        )}
      </Box>
      {!active && <Chip size="small" label="Inactive" />}
      {active && onDeactivate && (
        <Button size="small" color="error" onClick={onDeactivate}>
          {deactivateLabel}
        </Button>
      )}
    </Box>
  );
}

function RoomsTab({ rooms, departments, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    code: "",
    department_id: "",
    open_time: "08:00",
    close_time: "18:00",
    daily_ceiling: "",
    location: "",
  });

  return (
    <MasterList
      title="Operating rooms"
      subtitle="The daily ceiling warns; it does not block."
      addLabel="Add a theatre"
      onAdd={() => setOpen(true)}
    >
      {rooms.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No theatres configured.
        </Typography>
      ) : (
        rooms.map((r) => (
          <Row
            key={r.id}
            active={r.active}
            primary={`${r.name}${r.code ? ` (${r.code})` : ""}`}
            secondary={`${r.department_name || "no department"} · ${
              r.open_time ? `${r.open_time}–${r.close_time}` : "no working hours"
            }${r.daily_ceiling ? ` · ceiling ${r.daily_ceiling}` : ""}${
              r.location ? ` · ${r.location}` : ""
            }`}
            onDeactivate={async () => {
              if (report(await deactivateRoom(r.id))) onDone();
            }}
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add an operating room</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Name">
              <IpdTextField
                fullWidth
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Code">
              <IpdTextField
                fullWidth
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Department">
              <OtSelect
                placeholder="None"
                value={form.department_id}
                onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
                options={departments.map((d) => ({ value: d.id, label: d.name }))}
              />
            </IpdFormField>
            <IpdFormField label="Location">
              <IpdTextField
                fullWidth
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Opens">
              <IpdTextField
                fullWidth
                type="time"
                value={form.open_time}
                onChange={(e) => setForm((f) => ({ ...f, open_time: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Closes">
              <IpdTextField
                fullWidth
                type="time"
                value={form.close_time}
                onChange={(e) => setForm((f) => ({ ...f, close_time: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Daily case ceiling">
              <IpdTextField
                fullWidth
                type="number"
                value={form.daily_ceiling}
                onChange={(e) => setForm((f) => ({ ...f, daily_ceiling: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.name.trim()}
            onClick={async () => {
              if (report(await createRoom(form))) {
                setOpen(false);
                setForm({ ...form, name: "", code: "", location: "" });
                onDone();
              }
            }}
          >
            Add
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

const VOCABS = [
  { key: "anaesthesia_types", label: "Anaesthesia types" },
  { key: "surgery_positions", label: "Surgery positions" },
  { key: "incision_types", label: "Incision types" },
];

function VocabTab({ setup, onDone }) {
  const [adding, setAdding] = useState(null); // the vocab key being added to
  const [name, setName] = useState("");

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      {VOCABS.map((v) => (
        <MasterList
          key={v.key}
          title={v.label}
          addLabel="Add"
          onAdd={() => {
            setAdding(v.key);
            setName("");
          }}
        >
          {(setup?.[v.key] || []).length === 0 ? (
            <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
              Nothing configured.
            </Typography>
          ) : (
            setup[v.key].map((t) => (
              <Row
                key={t.id}
                primary={t.name}
                active={t.active}
                onDeactivate={async () => {
                  if (report(await deactivateVocab(v.key, t.id))) onDone();
                }}
              />
            ))
          )}
        </MasterList>
      ))}

      <Dialog open={Boolean(adding)} onClose={() => setAdding(null)} fullWidth maxWidth="xs">
        <DialogTitle>Add to {VOCABS.find((v) => v.key === adding)?.label || ""}</DialogTitle>
        <DialogContent>
          <IpdFormField label="Name">
            <IpdTextField
              fullWidth
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </IpdFormField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(null)}>Cancel</Button>
          <IpdButton
            disabled={!name.trim()}
            onClick={async () => {
              if (report(await createVocab(adding, { name }))) {
                setAdding(null);
                onDone();
              }
            }}
          >
            Add
          </IpdButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AttendeeTab({ types, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    is_mandatory: false,
    grading_required: false,
    display_order: "0",
  });

  return (
    <MasterList
      title="Case roles"
      subtitle="The jobs on an operating team, and which are compulsory."
      addLabel="Add a role"
      onAdd={() => setOpen(true)}
    >
      {types.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No roles configured.
        </Typography>
      ) : (
        types.map((t) => (
          <Row
            key={t.id}
            primary={t.name}
            active={t.active}
            secondary={`order ${t.display_order}${t.is_mandatory ? " · mandatory" : ""}${
              t.grading_required ? " · grade recorded" : ""
            }`}
            onDeactivate={async () => {
              if (report(await deactivateAttendeeType(t.id))) onDone();
            }}
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add a case role</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Name">
              <IpdTextField
                fullWidth
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Display order">
              <IpdTextField
                fullWidth
                type="number"
                value={form.display_order}
                onChange={(e) => setForm((f) => ({ ...f, display_order: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Mandatory on a case?">
              <OtSelect
                value={form.is_mandatory ? "yes" : "no"}
                onChange={(e) => setForm((f) => ({ ...f, is_mandatory: e.target.value === "yes" }))}
                options={[
                  { value: "no", label: "No" },
                  { value: "yes", label: "Yes — flag when unfilled" },
                ]}
              />
            </IpdFormField>
            <IpdFormField label="Record a grade?">
              <OtSelect
                value={form.grading_required ? "yes" : "no"}
                onChange={(e) =>
                  setForm((f) => ({ ...f, grading_required: e.target.value === "yes" }))
                }
                options={[
                  { value: "no", label: "No" },
                  { value: "yes", label: "Yes" },
                ]}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.name.trim()}
            onClick={async () => {
              if (report(await createAttendeeType(form))) {
                setOpen(false);
                setForm({ ...form, name: "" });
                onDone();
              }
            }}
          >
            Add
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

function BlocksTab({ blocks, rooms, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    operating_room_id: "",
    start_datetime: "",
    end_datetime: "",
    blocked_for: "MAINTENANCE",
    surgeon_name: "",
    reason: "",
  });

  return (
    <MasterList
      title="Room blocks"
      subtitle="Takes a theatre off the bookable timeline."
      addLabel="Block a theatre"
      onAdd={() => setOpen(true)}
    >
      {blocks.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No active blocks.
        </Typography>
      ) : (
        blocks.map((b) => (
          <Row
            key={b.id}
            primary={`${rooms.find((r) => r.id === b.operating_room_id)?.name || "Theatre"} — ${
              b.blocked_for === "SURGEON" ? b.surgeon_name || "surgeon" : "maintenance"
            }`}
            secondary={`${formatIstDateTime(b.start_datetime)} to ${formatIstDateTime(
              b.end_datetime
            )}${b.reason ? ` · ${b.reason}` : ""}`}
            onDeactivate={async () => {
              if (report(await cancelRoomBlock(b.id))) onDone();
            }}
            deactivateLabel="Cancel block"
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Block a theatre</DialogTitle>
        <DialogContent>
          <IpdFormField label="Theatre">
            <OtSelect
              placeholder="Choose a theatre"
              value={form.operating_room_id}
              onChange={(e) => setForm((f) => ({ ...f, operating_room_id: e.target.value }))}
              options={rooms.filter((r) => r.active).map((r) => ({ value: r.id, label: r.name }))}
            />
          </IpdFormField>
          <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <IpdFormField label="From">
              <IpdTextField
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.start_datetime}
                onChange={(e) => setForm((f) => ({ ...f, start_datetime: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="To">
              <IpdTextField
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.end_datetime}
                onChange={(e) => setForm((f) => ({ ...f, end_datetime: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Why">
              <OtSelect
                value={form.blocked_for}
                onChange={(e) => setForm((f) => ({ ...f, blocked_for: e.target.value }))}
                options={[
                  { value: "MAINTENANCE", label: "Maintenance" },
                  { value: "SURGEON", label: "Reserved for a surgeon" },
                ]}
              />
            </IpdFormField>
            {form.blocked_for === "SURGEON" && (
              <IpdFormField label="Surgeon">
                <IpdTextField
                  fullWidth
                  value={form.surgeon_name}
                  onChange={(e) => setForm((f) => ({ ...f, surgeon_name: e.target.value }))}
                />
              </IpdFormField>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <IpdFormField label="Reason">
              <IpdTextField
                fullWidth
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.operating_room_id || !form.start_datetime || !form.end_datetime}
            onClick={async () => {
              const payload = {
                ...form,
                start_datetime: new Date(form.start_datetime).toISOString(),
                end_datetime: new Date(form.end_datetime).toISOString(),
              };
              if (report(await createRoomBlock(payload))) {
                setOpen(false);
                onDone();
              }
            }}
          >
            Block
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

function BillingTab({ parameters, departments, onDone }) {
  const [open, setOpen] = useState(false);
  const [service, setService] = useState(null);
  const [form, setForm] = useState({
    name: "",
    department_id: "",
    service_code: "",
    medical_service_id: "",
    charge_type: "FIXED",
    revenue_category: "",
    report_category: "",
  });

  // A parameter with a department IS that department's theatre rate, and is
  // what lets theatre time bill itself at check-out. One without a department
  // is ordinary configuration over a service. Two different jobs, so they are
  // shown as two lists rather than one mixed one.
  const rates = parameters.filter((p) => p.department_id);
  const other = parameters.filter((p) => !p.department_id);
  const unmapped = departments.filter(
    (d) => !rates.some((r) => String(r.department_id) === String(d.id)),
  );

  return (
    <MasterList
      title="Theatre rates by department"
      subtitle="What an hour in theatre costs. Prices come from the EMR's Operation Theatre services — this only says which one applies."
      addLabel="Map a department"
      onAdd={() => setOpen(true)}
    >
      {rates.length === 0 ? (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 1,
            backgroundColor: IPD_COLORS.warning.bg,
            color: IPD_COLORS.warning.text,
            fontSize: 13.5,
          }}
        >
          No department has a theatre rate, so theatre time is not being billed.
          Map one and every case in that department charges its room automatically
          when the patient leaves theatre.
        </Box>
      ) : (
        rates.map((p) => (
          <Row
            key={p.id}
            primary={p.department_name || `Department #${p.department_id}`}
            active={p.active}
            secondary={`${p.service_code || "no service"} — billed per 30 minutes off the theatre clock`}
            onDeactivate={async () => {
              if (report(await deactivateBillingParameter(p.id))) onDone();
            }}
          />
        ))
      )}

      {/* Named plainly: these are the departments whose theatre time silently
          is not being charged. */}
      {rates.length > 0 && unmapped.length > 0 && (
        <Typography sx={{ mt: 1.5, fontSize: 12.5, color: IPD_COLORS.warning.text }}>
          Not billed yet: {unmapped.map((d) => d.name).join(", ")}
        </Typography>
      )}

      {other.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: "0.04em",
              color: IPD_COLORS.textSecondary,
              mb: 0.5,
            }}
          >
            OTHER SERVICE CONFIGURATION
          </Typography>
          {other.map((p) => (
            <Row
              key={p.id}
              primary={`${p.name} · ${p.charge_type}`}
              active={p.active}
              secondary={`${p.service_code || "no service code"}${
                p.revenue_category ? ` · ${p.revenue_category}` : ""
              }${p.report_category ? ` / ${p.report_category}` : ""}`}
              onDeactivate={async () => {
                if (report(await deactivateBillingParameter(p.id))) onDone();
              }}
            />
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Map a department to a theatre rate</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
            Theatres are named by size and the price list is by specialty, so the
            department decides the rate — a knee replacement bills at the
            orthopaedic rate whichever theatre it runs in. Once mapped, every case
            in that department charges its room automatically when the patient
            leaves theatre.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Department">
              <OtSelect
                placeholder="Choose a department"
                value={form.department_id}
                onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
                options={(departments || []).map((d) => ({ value: d.id, label: d.name }))}
              />
            </IpdFormField>
            <IpdFormField label="Charge type">
              <OtSelect
                value={form.charge_type}
                onChange={(e) => setForm((f) => ({ ...f, charge_type: e.target.value }))}
                options={[
                  { value: "FIXED", label: "Fixed" },
                  { value: "BUNDLED", label: "Bundled" },
                ]}
              />
            </IpdFormField>
          </Box>
          <Box sx={{ mt: 2 }}>
            {/* The rate itself is a service-master row. Its 30-minute fee shows
                in the list as it is searched, so the price is visible at the
                moment it is chosen rather than after the first bill. */}
            <ChargeServiceAutocomplete
              label="Theatre room service"
              required
              placeholder="Search theatre rooms…"
              classificationName={CLASSIFICATION_OT_ROOMS}
              value={service}
              onChange={(item) => {
                setService(item);
                setForm((f) => ({
                  ...f,
                  service_code: item ? item.service_code || "" : "",
                  medical_service_id: item ? item.medical_service_id || item.id : "",
                  name: item
                    ? `${(departments || []).find(
                        (d) => String(d.id) === String(f.department_id),
                      )?.name || "Theatre"} — ${item.name}`
                    : f.name,
                }));
              }}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2 }}>
            <IpdFormField label="Revenue category">
              <IpdTextField
                fullWidth
                value={form.revenue_category}
                onChange={(e) => setForm((f) => ({ ...f, revenue_category: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Report category">
              <IpdTextField
                fullWidth
                value={form.report_category}
                onChange={(e) => setForm((f) => ({ ...f, report_category: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.name.trim()}
            onClick={async () => {
              if (report(await createBillingParameter(form))) {
                setOpen(false);
                setForm({ ...form, name: "", service_code: "" });
                onDone();
              }
            }}
          >
            Add
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

function MaintenanceTab({ logs, rooms, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    operating_room_id: "",
    log_type: "FUMIGATION",
    outcome: "",
    notes: "",
  });

  return (
    <MasterList
      title="Maintenance, fumigation and sterilisation"
      subtitle="Cleaning, fumigation and sterilisation, per theatre."
      addLabel="Record"
      onAdd={() => setOpen(true)}
    >
      {logs.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          Nothing logged.
        </Typography>
      ) : (
        logs.map((l) => (
          <Row
            key={l.id}
            primary={`${l.operating_room_name} — ${l.log_type}`}
            secondary={`${formatIstDateTime(l.performed_at)}${
              l.performed_by_name ? ` · ${l.performed_by_name}` : ""
            }${l.outcome ? ` · ${l.outcome}` : ""}${l.notes ? ` · ${l.notes}` : ""}`}
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Record theatre maintenance</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Theatre">
              <OtSelect
                placeholder="Choose a theatre"
                value={form.operating_room_id}
                onChange={(e) => setForm((f) => ({ ...f, operating_room_id: e.target.value }))}
                options={rooms.map((r) => ({ value: r.id, label: r.name }))}
              />
            </IpdFormField>
            <IpdFormField label="What was done">
              <OtSelect
                value={form.log_type}
                onChange={(e) => setForm((f) => ({ ...f, log_type: e.target.value }))}
                options={MAINTENANCE_TYPES}
              />
            </IpdFormField>
            <IpdFormField label="Outcome">
              <IpdTextField
                fullWidth
                value={form.outcome}
                onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Notes">
              <IpdTextField
                fullWidth
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.operating_room_id}
            onClick={async () => {
              if (report(await recordMaintenance(form))) {
                setOpen(false);
                onDone();
              }
            }}
          >
            Record
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}


/**
 * The theatre environment register (NABH).
 *
 * Temperature and humidity daily, pressure differential at least monthly. The
 * maintenance log above covers the EVENTS — cleaning, fumigation, sterilisation
 * — and this covers the standing readings, which is usually the first register
 * an assessor asks to see and the one a theatre is least likely to have.
 *
 * COVERAGE IS THE HEADLINE, not the readings. The finding at an assessment is
 * almost never a bad reading — those get noticed and fixed on the day. It is a
 * day nobody wrote anything down, which is invisible in a list of readings and
 * obvious in a list of gaps. So the gaps come first.
 *
 * Whether a reading is in range is decided by the server and shown here; it is
 * never computed in the browser, for the same reason the count result is not.
 */
function EnvironmentTab({ environment, rooms, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    operating_room_id: "",
    temperature_c: "",
    humidity_pct: "",
    pressure_differential_pa: "",
    air_changes_per_hour: "",
    action_taken: "",
  });

  const rows = environment?.rows || [];
  const coverage = environment?.coverage || [];
  const gaps = coverage.filter((c) => !c.complete);
  const bands = environment?.bands || {};

  const empty = ["temperature_c", "humidity_pct", "pressure_differential_pa", "air_changes_per_hour"]
    .every((k) => !String(form[k]).trim());

  return (
    <MasterList
      title="Theatre environment"
      subtitle="Temperature and humidity daily, pressure differential at least monthly."
      addLabel="Record a reading"
      onAdd={() => setOpen(true)}
    >
      {gaps.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: IPD_COLORS.warning.bg,
            color: IPD_COLORS.warning.text,
            fontSize: 13,
          }}
        >
          <strong>Days with no reading</strong>
          {gaps.map((c) => (
            <Box key={c.operating_room_id} sx={{ mt: 0.5 }}>
              {c.operating_room_name}: {c.missing_days.join(", ")}
            </Box>
          ))}
        </Box>
      )}

      {environment?.breach_count > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: IPD_COLORS.error.bg,
            color: IPD_COLORS.error.text,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {environment.breach_count} reading(s) out of range over this period.
        </Box>
      )}

      {bands.temperature_c && (
        <Typography sx={{ mb: 1.5, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          In range: {bands.temperature_c[0]}&ndash;{bands.temperature_c[1]}&deg;C,{" "}
          {bands.humidity_pct[0]}&ndash;{bands.humidity_pct[1]}% humidity, at least{" "}
          {bands.pressure_differential_pa_min} Pa positive pressure.
        </Typography>
      )}

      {rows.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No readings recorded for today.
        </Typography>
      ) : (
        rows.map((r) => (
          <Row
            key={r.id}
            primary={`${r.operating_room_name} — ${r.reading_date}`}
            secondary={
              [
                r.temperature_c ? `${r.temperature_c}\u00b0C` : null,
                r.humidity_pct ? `${r.humidity_pct}% RH` : null,
                r.pressure_differential_pa ? `${r.pressure_differential_pa} Pa` : null,
                r.air_changes_per_hour ? `${r.air_changes_per_hour} ACH` : null,
                r.recorded_by_name,
                r.within_range === false ? `OUT OF RANGE — ${r.out_of_range_note}` : null,
                r.action_taken,
              ]
                .filter(Boolean)
                .join(" · ")
            }
          />
        ))
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Record a theatre environment reading</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <Box sx={{ gridColumn: "span 2" }}>
              <IpdFormField label="Theatre">
                <OtSelect
                  placeholder="Choose a theatre"
                  value={form.operating_room_id}
                  onChange={(e) => setForm((f) => ({ ...f, operating_room_id: e.target.value }))}
                  options={rooms.map((r) => ({ value: r.id, label: r.name }))}
                />
              </IpdFormField>
            </Box>
            <IpdFormField label="Temperature (&deg;C)">
              <IpdTextField
                fullWidth
                value={form.temperature_c}
                onChange={(e) => setForm((f) => ({ ...f, temperature_c: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Humidity (%)">
              <IpdTextField
                fullWidth
                value={form.humidity_pct}
                onChange={(e) => setForm((f) => ({ ...f, humidity_pct: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Pressure differential (Pa)">
              <IpdTextField
                fullWidth
                placeholder="Monthly — leave blank on a routine daily reading"
                value={form.pressure_differential_pa}
                onChange={(e) =>
                  setForm((f) => ({ ...f, pressure_differential_pa: e.target.value }))
                }
              />
            </IpdFormField>
            <IpdFormField label="Air changes per hour">
              <IpdTextField
                fullWidth
                value={form.air_changes_per_hour}
                onChange={(e) => setForm((f) => ({ ...f, air_changes_per_hour: e.target.value }))}
              />
            </IpdFormField>
            <Box sx={{ gridColumn: "span 2" }}>
              <IpdFormField label="If it was out of range, what was done">
                <IpdTextField
                  fullWidth
                  value={form.action_taken}
                  onChange={(e) => setForm((f) => ({ ...f, action_taken: e.target.value }))}
                />
              </IpdFormField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={!form.operating_room_id || empty}
            onClick={async () => {
              if (report(await recordEnvironmentReading(form))) {
                setOpen(false);
                onDone();
              }
            }}
          >
            Record
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}

/**
 * Note templates — named default text a surgeon applies by hand.
 *
 * Maintained here rather than by each surgeon so a theatre has one agreed
 * starting point per operation. Retiring frees the name, which is what lets a
 * hospital replace "TKR standard" without inventing "TKR standard v2".
 *
 * The field list comes from the SERVER, per note kind, so a template can only
 * carry fields that note actually has. A template with a field the note does
 * not have would silently drop on apply and look like the template had failed.
 */
function TemplatesTab({ templates, availableFields, onDone }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("surgical");
  const [name, setName] = useState("");
  const [fields, setFields] = useState({});
  const [available, setAvailable] = useState([]);
  const [busy, setBusy] = useState(false);

  // Which fields this note kind has. Served with the templates rather than
  // hard-coded, so a field added to the backend appears here without a change
  // and a template can never carry one the note does not have.
  useEffect(() => {
    setAvailable(availableFields[kind] || []);
    setFields({});
  }, [kind, availableFields]);

  const save = async () => {
    const filled = Object.fromEntries(
      Object.entries(fields).filter(([, v]) => String(v).trim())
    );
    if (!name.trim() || !Object.keys(filled).length) return;
    setBusy(true);
    try {
      if (report(await createNoteTemplate({ name: name.trim(), note_kind: kind, fields: filled }))) {
        setOpen(false);
        setName("");
        setFields({});
        onDone();
      }
    } finally {
      setBusy(false);
    }
  };

  const byKind = {};
  (templates || []).forEach((t) => {
    (byKind[t.note_kind] = byKind[t.note_kind] || []).push(t);
  });

  return (
    <MasterList
      title="Note templates"
      subtitle="Applied by hand when a note is written. Nothing fills itself."
      addLabel="New template"
      onAdd={() => setOpen(true)}
    >
      {(templates || []).length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No templates yet.
        </Typography>
      ) : (
        ["surgical", "anaesthesia", "nursing"].map((k) =>
          byKind[k] ? (
            <Box key={k} sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  color: IPD_COLORS.textSecondary,
                  mb: 0.5,
                }}
              >
                {k.toUpperCase()}
              </Typography>
              {byKind[k].map((t) => (
                <Row
                  key={t.id}
                  primary={t.name}
                  secondary={`${Object.keys(t.fields || {}).length} field(s) filled`}
                  onDeactivate={async () => {
                    if (report(await retireNoteTemplate(t.id))) onDone();
                  }}
                  deactivateLabel="Retire"
                />
              ))}
            </Box>
          ) : null
        )
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>New note template</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
            <IpdFormField label="Name">
              <IpdTextField
                fullWidth
                placeholder="e.g. TKR standard"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </IpdFormField>
            <IpdFormField label="Which note">
              <OtSelect
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                options={[
                  { value: "surgical", label: "Surgical" },
                  { value: "anaesthesia", label: "Anaesthesia" },
                  { value: "nursing", label: "Nursing" },
                ]}
              />
            </IpdFormField>
          </Box>

          <Typography sx={{ mt: 2.5, mb: 1, fontSize: 12, fontWeight: 700 }}>
            DEFAULT TEXT — leave a field blank and the template will not touch it
          </Typography>
          <Box sx={{ display: "grid", gap: 1.5 }}>
            {available.map((f) => (
              <IpdFormField key={f} label={humanise(f)}>
                <IpdTextField
                  fullWidth
                  multiline
                  minRows={1}
                  value={fields[f] || ""}
                  onChange={(e) => setFields((s) => ({ ...s, [f]: e.target.value }))}
                />
              </IpdFormField>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={busy || !name.trim() || !Object.values(fields).some((v) => String(v).trim())}
            onClick={save}
          >
            Save template
          </IpdButton>
        </DialogActions>
      </Dialog>
    </MasterList>
  );
}
